import * as StateMachine from 'javascript-state-machine';

import type {HeartbeatService} from '../core/HeartbeatService';
import {Event, JSM_INITIAL_EVENT} from '../enums/Event';
import {createVideoStartFailedReason} from '../enums/VideoStartFailedReason';
import type {AnalyticsStateMachineOptions} from '../types/AnalyticsStateMachineOptions';
import type {AnalyticsEventBase} from '../types/EventData';
import type {StateMachineCallbacks} from '../types/StateMachineCallbacks';
import {logger} from '../utils/Logger';
import {isFunction, isNumber} from '../utils/Utils';

import {AnalyticsStateMachine} from './AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  logMissingCallbackWarning,
  logStateMachineTransition,
  on,
  whileIn,
} from './stateMachineUtils';

enum State {
  AD = 'AD',
  AUDIOTRACK_CHANGING = 'AUDIOTRACK_CHANGING',
  CASTING = 'CASTING',
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  END = 'END',
  ERROR = 'ERROR',
  MUTING_PAUSE = 'MUTING_PAUSE',
  MUTING_PLAY = 'MUTING_PLAY',
  MUTING_READY = 'MUTING_READY',
  PAUSE = 'PAUSE',
  PAUSED_SEEKING = 'PAUSED_SEEKING',
  PLAYING = 'PLAYING',
  QUALITYCHANGE = 'QUALITYCHANGE',
  QUALITYCHANGE_PAUSE = 'QUALITYCHANGE_PAUSE',
  QUALITYCHANGE_REBUFFERING = 'QUALITYCHANGE_REBUFFERING',
  READY = 'READY',
  REBUFFERING = 'REBUFFERING',
  SETUP = 'SETUP',
  SETUP_SOURCE_CHANGE = 'SETUP_SOURCE_CHANGE',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  SUBTITLE_CHANGING = 'SUBTITLE_CHANGING',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class HTML5AnalyticsStateMachine extends AnalyticsStateMachine {
  constructor(
    stateMachineCallbacks: StateMachineCallbacks,
    playingHeartbeatService: HeartbeatService,
    opts: AnalyticsStateMachineOptions,
  ) {
    super(stateMachineCallbacks, playingHeartbeatService, opts);

    this.createStateMachine(opts);
  }

  getAllStates() {
    return [
      ...Object.keys(State).map((key) => State[key]),
      'FINISH_QUALITYCHANGE_PAUSE',
      'FINISH_QUALITYCHANGE',
      'FINISH_QUALITYCHANGE_REBUFFERING',
    ];
  }

  getAllStatesBut(states: string[]) {
    return this.getAllStates().filter((i) => states.indexOf(i) < 0);
  }

  override createStateMachine(opts: AnalyticsStateMachineOptions) {
    return StateMachine.create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        {
          name: Event.SOURCE_LOADED,
          from: [State.SETUP, State.SETUP_SOURCE_CHANGE, State.SOURCE_CHANGING],
          to: State.READY,
        },

        // just passing since it's pushed in a live stream
        ...whileIn(State.READY).passOver(Event.SEEK, Event.SEEKED),

        on(Event.TIMECHANGED).stayIn(State.SETUP),
        on(Event.SOURCE_LOADED).stayIn(State.READY),

        {name: Event.PLAY, from: State.READY, to: State.STARTUP},
        // play attempt during sourceChanging
        {name: Event.PLAY, from: State.SOURCE_CHANGING, to: State.STARTUP},

        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.UNLOAD, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        ...whileIn(State.STARTUP).passOver(
          Event.SOURCE_LOADED, // when we have autoplay we will receive SOURCE_LOADED after we enter the STARTUP state
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          Event.VIDEO_CHANGE,
          Event.AUDIO_CHANGE,
          Event.SEEK, // just passing since it's pushed in a live stream
          Event.SEEKED, // just passing since it's pushed in a live stream
        ),

        {name: Event.TIMECHANGED, from: State.READY, to: State.STARTUP},
        {name: Event.TIMECHANGED, from: State.STARTUP, to: State.PLAYING},
        ...whileIn(State.PLAYING).passOver(Event.TIMECHANGED),

        on(Event.SEEKED).stayIn(State.PAUSE),

        on(Event.END_BUFFERING).stayIn(State.PLAYING),
        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.REBUFFERING},
        on(Event.START_BUFFERING).stayIn(State.REBUFFERING),

        {name: Event.PLAY, from: State.REBUFFERING, to: State.PLAYING},
        {name: Event.TIMECHANGED, from: State.REBUFFERING, to: State.PLAYING},

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PAUSE, from: State.REBUFFERING, to: State.PAUSE},

        {name: Event.PLAY, from: State.PAUSE, to: State.PLAYING},
        {name: Event.TIMECHANGED, from: State.PAUSE, to: State.PLAYING},

        {name: Event.VIDEO_CHANGE, from: State.PLAYING, to: State.QUALITYCHANGE},
        {name: Event.AUDIO_CHANGE, from: State.PLAYING, to: State.QUALITYCHANGE},
        ...whileIn(State.QUALITYCHANGE).runEventCallbacksFor(Event.VIDEO_CHANGE, Event.AUDIO_CHANGE),
        {name: 'FINISH_QUALITYCHANGE', from: State.QUALITYCHANGE, to: State.PLAYING},

        {name: Event.VIDEO_CHANGE, from: State.PAUSE, to: State.QUALITYCHANGE_PAUSE},
        {name: Event.AUDIO_CHANGE, from: State.PAUSE, to: State.QUALITYCHANGE_PAUSE},
        ...whileIn(State.QUALITYCHANGE_PAUSE).runEventCallbacksFor(Event.VIDEO_CHANGE, Event.AUDIO_CHANGE),
        {name: 'FINISH_QUALITYCHANGE_PAUSE', from: State.QUALITYCHANGE_PAUSE, to: State.PAUSE},

        {name: Event.SEEK, from: State.PAUSE, to: State.PAUSED_SEEKING},
        ...whileIn(State.PAUSED_SEEKING).passOver(
          Event.PLAY,
          Event.SEEK,
          Event.AUDIO_CHANGE,
          Event.VIDEO_CHANGE,
          Event.START_BUFFERING,
          Event.END_BUFFERING,
        ),

        {name: Event.SEEKED, from: State.PAUSED_SEEKING, to: State.PAUSE},
        {name: Event.TIMECHANGED, from: State.PAUSED_SEEKING, to: State.PLAYING},
        {name: Event.PAUSE, from: State.PAUSED_SEEKING, to: State.PAUSE},

        {name: Event.END, from: State.PAUSED_SEEKING, to: State.END},
        {name: Event.END, from: State.PLAYING, to: State.END},
        {name: Event.END, from: State.PAUSE, to: State.END},
        ...whileIn(State.END).passOver(
          Event.PAUSE,
          Event.SEEK,
          Event.SEEKED,
          Event.TIMECHANGED,
          Event.END_BUFFERING,
          Event.START_BUFFERING,
          Event.END,
        ),

        // just passing since it's pushed in a live stream
        ...whileIn(State.PLAYING).passOver(Event.SEEK, Event.SEEKED),

        {name: Event.PLAY, from: State.END, to: State.PLAYING},

        {name: Event.ERROR, from: this.getAllStatesBut([State.STARTUP]), to: State.ERROR},
        {name: Event.PAUSE, from: State.ERROR, to: State.ERROR},

        {name: Event.UNLOAD, from: this.getAllStatesBut([State.STARTUP]), to: State.END},

        {name: Event.SUBTITLE_CHANGE, from: State.PLAYING, to: State.SUBTITLE_CHANGING},

        on(Event.SUBTITLE_CHANGE).stayIn(State.PAUSE),
        on(Event.SUBTITLE_CHANGE).stayIn(State.READY),
        on(Event.SUBTITLE_CHANGE).stayIn(State.STARTUP),
        on(Event.SUBTITLE_CHANGE).stayIn(State.REBUFFERING),
        on(Event.SUBTITLE_CHANGE).stayIn(State.SUBTITLE_CHANGING),

        {name: Event.TIMECHANGED, from: State.SUBTITLE_CHANGING, to: State.PLAYING},

        {name: Event.AUDIOTRACK_CHANGED, from: State.PLAYING, to: State.AUDIOTRACK_CHANGING},

        on(Event.AUDIOTRACK_CHANGED).stayIn(State.PAUSE),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.READY),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.STARTUP),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.REBUFFERING),
        on(Event.AUDIOTRACK_CHANGED).stayIn(State.AUDIOTRACK_CHANGING),
        {name: Event.TIMECHANGED, from: State.AUDIOTRACK_CHANGING, to: State.PLAYING},

        {name: Event.START_AD, from: State.PLAYING, to: State.AD},
        {name: Event.END_AD, from: State.AD, to: State.PLAYING},

        {name: Event.MUTE, from: State.READY, to: State.MUTING_READY},
        {name: Event.UN_MUTE, from: State.READY, to: State.MUTING_READY},
        {name: 'FINISH_MUTING', from: State.MUTING_READY, to: State.READY},

        {name: Event.MUTE, from: State.PLAYING, to: State.MUTING_PLAY},
        {name: Event.UN_MUTE, from: State.PLAYING, to: State.MUTING_PLAY},
        {name: 'FINISH_MUTING', from: State.MUTING_PLAY, to: State.PLAYING},

        {name: Event.MUTE, from: State.PAUSE, to: State.MUTING_PAUSE},
        {name: Event.UN_MUTE, from: State.PAUSE, to: State.MUTING_PAUSE},
        {name: 'FINISH_MUTING', from: State.MUTING_PAUSE, to: State.PAUSE},

        {name: Event.START_CAST, from: [State.READY, State.PAUSE], to: State.CASTING},
        ...whileIn(State.CASTING).passOver(
          Event.PAUSE,
          Event.PLAY,
          Event.TIMECHANGED,
          Event.MUTE,
          Event.SEEK,
          Event.SEEKED,
        ),

        {name: Event.END_CAST, from: State.CASTING, to: State.READY},

        {name: Event.MANUAL_SOURCE_CHANGE, from: State.SETUP, to: State.SETUP_SOURCE_CHANGE},
        on(Event.MANUAL_SOURCE_CHANGE).stayIn(State.SETUP_SOURCE_CHANGE),
        {
          name: Event.MANUAL_SOURCE_CHANGE,
          from: this.getAllStatesBut([State.SETUP, State.SETUP_SOURCE_CHANGE]),
          to: State.SOURCE_CHANGING,
        },
        {name: Event.SOURCE_UNLOADED, from: this.getAllStates(), to: State.SOURCE_CHANGING},

        on(Event.TIMECHANGED).stayIn(State.SOURCE_CHANGING),
        on(Event.PAUSE).stayIn(State.SOURCE_CHANGING),

        {name: Event.VIDEO_CHANGE, from: State.REBUFFERING, to: State.QUALITYCHANGE_REBUFFERING},
        {name: Event.AUDIO_CHANGE, from: State.REBUFFERING, to: State.QUALITYCHANGE_REBUFFERING},
        ...whileIn(State.QUALITYCHANGE_REBUFFERING).runEventCallbacksFor(Event.VIDEO_CHANGE, Event.AUDIO_CHANGE),
        {name: 'FINISH_QUALITYCHANGE_REBUFFERING', from: State.QUALITYCHANGE_REBUFFERING, to: State.REBUFFERING},

        // Because of this state transition, we are able to male "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},
      ],

      // Keep callbacks sorted by order in which they are called by JSM library (and after order, sorted by name)
      //
      // Do not use convenience, shortened callbacks
      //  - instead of `onEVENT` use `onafterEVENT`
      //  - instead of `onSTATE` use `onenterSTATE`
      //
      // see: https://github.com/jakesgordon/javascript-state-machine/blob/2.4.0/README.md#callbacks
      callbacks: {
        [`onleave${State.PLAYING}`]: (_event, _from, _to, _eventTimestamp, _eventData) => {
          this.playingHeartbeatService.stopHeartbeat();
        },

        [`onleave${State.STARTUP}`]: (_event, _from, _to, _eventTimestamp, _eventData) => {
          this.clearVideoStartTimeout();
        },

        onleavestate: (event, from, to, eventTimestamp, eventData) => {
          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          if (from === State.REBUFFERING) {
            this.resetRebufferingHelpers();
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          // player errors will be sent via eventCallback() -> callEvent() -> onPlayerError() function chain
          // if then sourceChange is called same error will be sent second time
          if (from === State.ERROR && (event === Event.MANUAL_SOURCE_CHANGE || event === Event.SOURCE_UNLOADED)) {
            return;
          }

          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;

          if (eventData && to !== State.PAUSED_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          const lowerCaseFrom = String(from).toLowerCase();
          if (to === State.VIDEOSTART_FAILED) {
            this.clearVideoStartTimeout();
            this.stateMachineCallbacks.videoStartFailed(stateDuration, createVideoStartFailedReason(event));
          } else if (from === State.PAUSED_SEEKING) {
            this.stateMachineCallbacks[lowerCaseFrom](stateDuration, lowerCaseFrom, eventData);
          } else if (event === Event.UNLOAD) {
            this.stateMachineCallbacks.unload(stateDuration, lowerCaseFrom);
          } else if (from === State.PAUSE && to !== State.PAUSED_SEEKING) {
            this.stateMachineCallbacks.pause(stateDuration, lowerCaseFrom);
          } else {
            // Default "onleavestate" state machine callback
            //
            // Every time, when we leave a state, we should try to call "state" callback,
            // to be able to make "a cut" and send analytics sample (if necessary)
            const defaultStateMachineCallbackFn = this.stateMachineCallbacks[lowerCaseFrom];
            if (isFunction(defaultStateMachineCallbackFn)) {
              // a try-catch block to add more resilience against callback errors
              try {
                defaultStateMachineCallbackFn(stateDuration, lowerCaseFrom, eventData);
              } catch (e) {
                logger.error('Exception occurred in State Machine callback ' + lowerCaseFrom, eventData, e);
              }
            } else {
              // if callback is not defined, log a warning to indicate to the developer that
              //  - a new callback function needs to be implemented to relay on this default state machine callback
              //  - or if no callback is needed, we should
              //    - early return from this JSM callback
              //    - or add this state to the ignored list
              logMissingCallbackWarning(from, [State.READY, State.SOURCE_CHANGING, State.SETUP_SOURCE_CHANGE]);
            }
          }

          if (eventData && to !== State.PAUSED_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.VIDEO_CHANGE) {
            this.stateMachineCallbacks.videoChange(eventData);
          } else if (event === Event.AUDIO_CHANGE) {
            this.stateMachineCallbacks.audioChange(eventData);
          } else if (event === Event.MUTE) {
            this.stateMachineCallbacks.mute();
          } else if (event === Event.UN_MUTE) {
            this.stateMachineCallbacks.unMute();
          }
        },

        [`onenter${State.PLAYING}`]: (_event, _from, _to, eventTimestamp, _eventData) => {
          this.onEnterStateTimestamp = eventTimestamp;
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.SOURCE_CHANGING}`]: (event, _from, _to, _eventTimestamp, eventData) => {
          if (event === Event.MANUAL_SOURCE_CHANGE) {
            this.stateMachineCallbacks.manualSourceChange(eventData);
          }
        },

        [`onenter${State.STARTUP}`]: (_event, _from, _to, _eventTimestamp, _eventData) => {
          this.setVideoStartTimeout();
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData) => {
          logStateMachineTransition('ENTER', event, from, to);

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();
          // if customer set custom "start time" timestamp in AnalyticsStateMachineOptions of adapter constructor,
          // use it and override `onEnterStateTimestamp` during JSM initialization (transition to initial SETUP state)
          if (isNumber(opts.starttime) && event === JSM_INITIAL_EVENT) {
            this.onEnterStateTimestamp = opts.starttime;
          }

          if (eventData && to !== State.PAUSED_SEEKING) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (event === Event.START_CAST && to === State.CASTING) {
            this.stateMachineCallbacks.startCasting(eventTimestamp, eventData);
          }

          if (to === State.REBUFFERING) {
            this.startRebufferingHeartbeatInterval();
          }
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.error(eventData);
        },

        onafterevent: (_event, _from, to, eventTimestamp, _eventData) => {
          if (to === State.QUALITYCHANGE_PAUSE) {
            this.stateMachine.FINISH_QUALITYCHANGE_PAUSE(eventTimestamp);
          }
          if (to === State.QUALITYCHANGE) {
            this.stateMachine.FINISH_QUALITYCHANGE(eventTimestamp);
          }
          if (to === State.QUALITYCHANGE_REBUFFERING) {
            this.stateMachine.FINISH_QUALITYCHANGE_REBUFFERING(eventTimestamp);
          }
          if (to === State.MUTING_READY || to === State.MUTING_PLAY || to === State.MUTING_PAUSE) {
            this.stateMachine.FINISH_MUTING(eventTimestamp);
          }
        },
      },
    });
  }

  override onSsaiPlaybackInteraction(_timestamp: number, _eventObject: AnalyticsEventBase): void {
    // TODO: [AN-4076] Implement SSAI ad handling for html players
    //  See: https://bitmovin.atlassian.net/browse/AN-4076
  }
}
